import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from 'app/spacing';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import { HistoryModal } from '__components/Modals/HistoryModal';
import { useColumns } from 'components/Modals/HistoryModal/hooks';
import { employeeHistoryQuery } from 'query/employee';
import { permissionGroupHistoryQuery } from 'query/permission-group';
import { roleHistoryQuery } from 'query/role';
import { HistoryDetailsModal } from 'components/Modals/HistoryModal/HistoryDetailsModal';
import { EmployeeTimelineEntry } from '@kizen/api/employee';
import { RoleTimelineEntry } from '@kizen/api/role';
import { PermissionGroupTimelineEntry } from '@kizen/api/permission-group';

type HistoryModalProps = {
  id: string;
  onHide(): void;
};

type ActionOption = {
  value: 'view';
};

const DEFAULT_PAGINATION_SETTINGS = {
  page: 1,
  pageSize: 10,
  ordering: '-created',
};

export const EmployeeHistoryModal = ({ id, onHide }: HistoryModalProps) => {
  const { t } = useTranslation();
  const [excludeEventType, setExcludeEventType] = useState(['login']);
  const [history, setHistory] = useState<EmployeeTimelineEntry | null>(null);
  const isMobile = useBreakpoint();
  const pagination = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);

  const { page, pageSize, ordering, search } = pagination;

  const queryParams = {
    page,
    page_size: pageSize,
    ordering,
    search,
    exclude_event_type: excludeEventType,
  };

  const historyQuery = useQuery(
    employeeHistoryQuery(id, {
      params: queryParams,
      placeholderData: keepPreviousData,
      staleTime: 0,
    })
  );

  const handleSelectAction = useCallback(
    ({ value }: ActionOption, data: EmployeeTimelineEntry) => {
      if (value === 'view') {
        setHistory(data);
      }
    },
    []
  );

  const handleHideHistoryDetails = useCallback(() => {
    setHistory(null);
  }, []);

  const columns = useColumns(handleSelectAction);

  if (isMobile) {
    return null;
  }

  return (
    <>
      <HistoryModal
        show
        onHide={onHide}
        onConfirm={onHide}
        showEventTypeFilter
        heading={t('Team Member History Log')}
        loading={historyQuery.isLoading}
        results={historyQuery.data?.results ?? []}
        columns={columns}
        count={historyQuery.data?.count}
        pagination={pagination}
        showSearch
        setExcludeEventType={setExcludeEventType}
        excludeEventType={excludeEventType}
      />
      {history && (
        <HistoryDetailsModal
          data={history.data}
          onHide={handleHideHistoryDetails}
        />
      )}
    </>
  );
};

export const RoleHistoryModal = ({ id, onHide }: HistoryModalProps) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState<RoleTimelineEntry | null>(null);
  const isMobile = useBreakpoint();
  const pagination = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);

  const { page, pageSize, ordering, search } = pagination;

  const queryParams = {
    page,
    page_size: pageSize,
    ordering,
    search,
  };

  const historyQuery = useQuery(
    roleHistoryQuery(id, {
      params: queryParams,
      placeholderData: keepPreviousData,
      staleTime: 0,
    })
  );

  const handleSelectAction = useCallback(
    ({ value }: ActionOption, data: RoleTimelineEntry) => {
      if (value === 'view') {
        setHistory(data);
      }
    },
    []
  );

  const handleHideHistoryDetails = useCallback(() => {
    setHistory(null);
  }, []);

  const columns = useColumns(handleSelectAction);

  if (isMobile) {
    return null;
  }

  return (
    <>
      <HistoryModal
        show
        showSearch
        heading={t('Role History Log')}
        onHide={onHide}
        onConfirm={onHide}
        loading={historyQuery.isLoading}
        results={historyQuery.data?.results ?? []}
        columns={columns}
        count={historyQuery.data?.count}
        pagination={pagination}
      />
      {history && (
        <HistoryDetailsModal
          data={history.data}
          onHide={handleHideHistoryDetails}
        />
      )}
    </>
  );
};

export const PermissionGroupHistoryModal = ({
  id,
  onHide,
}: HistoryModalProps) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState<PermissionGroupTimelineEntry | null>(
    null
  );
  const isMobile = useBreakpoint();
  const pagination = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);

  const { page, pageSize, ordering, search } = pagination;

  const queryParams = {
    page,
    page_size: pageSize,
    ordering,
    search,
  };

  const historyQuery = useQuery(
    permissionGroupHistoryQuery(id, {
      params: queryParams,
      placeholderData: keepPreviousData,
      staleTime: 0,
    })
  );

  const handleSelectAction = useCallback(
    ({ value }: ActionOption, data: PermissionGroupTimelineEntry) => {
      if (value === 'view') {
        setHistory(data);
      }
    },
    []
  );

  const handleHideHistoryDetails = useCallback(() => {
    setHistory(null);
  }, []);

  const columns = useColumns(handleSelectAction);

  if (isMobile) {
    return null;
  }

  return (
    <>
      <HistoryModal
        show
        showSearch
        heading={t('Permission Group History Log')}
        onHide={onHide}
        onConfirm={onHide}
        loading={historyQuery.isLoading}
        results={historyQuery.data?.results ?? []}
        columns={columns}
        count={historyQuery.data?.count}
        pagination={pagination}
      />
      {history && (
        <HistoryDetailsModal
          data={history.data}
          onHide={handleHideHistoryDetails}
        />
      )}
    </>
  );
};
